/**
 * (owner, Sep 25: "add bigger billboards that could have logos or images") The billboards' faces and the big boards themselves.
 * A face is painted once into a small canvas per ad and shape (the brand's colours, its logo, its name and slogan — or its
 * picture from the media pipeline) and drawn onto the board with one transformed drawImage a frame: readable when zoomed in, a
 * coloured slab far out, the same picture on every board of the ad. The big boards (world/boards.ts) stand on steel posts, or
 * on a frame over a roof; they cast a shadow and their faces stay lit at night.
 */
import type { BoardSpec, Brand, Sprite } from '../content/types';
import { CanvasPainter } from '../draw/canvas-painter';
import { drawSprite, shade } from '../draw/painter';
import { boardCorners, boardPosts, type PlacedBoard } from '../world/boards';
import type { Camera } from './camera';

type Cv = HTMLCanvasElement | OffscreenCanvas;
/** what a face shows: a name, a line under it, two colours, a logo, a picture */
export interface FaceAd { key: string; name: string; line?: string; color: string; color2: string; logo?: Sprite; /** a picture's URL: it fills the face once loaded */ image?: string; /** the picture is the whole poster (a custom ad's upload): no panel with the logo and name over it */ plain?: boolean; /** small print in a corner (a custom ad's link) */ link?: string }
export const brandFace = (b: Brand, imageUrl?: string): FaceAd => ({ key: `brand:${b.id}`, name: b.name, line: b.slogan, color: b.color, color2: b.color2 ?? '#ffffff', logo: b.logo, image: imageUrl });

/** the face's width in canvas pixels (the height follows the board's shape): enough for a board filling a phone's screen */
const FACE_W = 480, FACE_CAP = 64;
/** the face stays this bright at night (the darkness is 0.55 × night; the board's floodlights put most of it back) */
const LIT = 0.75;
const mk = (w: number, h: number): Cv => (typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(w, h) : Object.assign(document.createElement('canvas'), { width: w, height: h }));

export class BoardArt {
  private faces = new Map<string, Cv>();
  private imgs = new Map<string, HTMLImageElement | null>();
  /** the lit faces drawn this frame (the night pass puts their light back): the transform, the size, the face */
  lit: { m: [number, number, number, number, number, number]; w: number; h: number; face: Cv; z: number; box: [number, number, number, number] }[] = [];
  /** a building drawn after (nearer than) a lit face, as tall or taller, over it on screen: that face's light stays off (it would shine through the building) */
  occlude(box: [number, number, number, number], height: number): void {
    this.lit = this.lit.filter((l) => l.z > height || l.box[2] < box[0] || l.box[0] > box[2] || l.box[3] < box[1] || l.box[1] > box[3]);
  }

  private image(url: string): HTMLImageElement | undefined {
    const had = this.imgs.get(url); if (had !== undefined) return had && had.complete && had.naturalWidth ? had : undefined;
    if (typeof Image === 'undefined') return undefined;
    const img = new Image(); img.crossOrigin = 'anonymous'; // (the media server answers with CORS: drawn without tainting the canvas the Photos app reads)
    img.onload = () => { for (const k of [...this.faces.keys()]) if (k.includes(url)) this.faces.delete(k); };
    img.onerror = () => this.imgs.set(url, null);
    img.src = url; this.imgs.set(url, img);
    return undefined;
  }

  /** the face for an ad on a board of this shape (width / depth), painted once */
  face(ad: FaceAd, aspect: number): Cv {
    const pic = ad.image ? this.image(ad.image) : undefined, a = Math.round(aspect * 10) / 10;
    const key = `${ad.key}|${a}|${pic ? ad.image : ''}`;
    let cv = this.faces.get(key); if (cv) return cv;
    const W = FACE_W, H = Math.max(24, Math.round(W / a));
    cv = mk(W, H); paintFace(cv.getContext('2d') as CanvasRenderingContext2D, W, H, ad, pic);
    if (this.faces.size >= FACE_CAP) this.faces.delete(this.faces.keys().next().value!);
    this.faces.set(key, cv);
    return cv;
  }
  /** drop the faces of these ads (a custom ad was edited) — or all of them */
  forget(prefix = ''): void { for (const k of [...this.faces.keys()]) if (k.startsWith(prefix)) this.faces.delete(k); }

  /** a face on a flat quad `w` × `h` tiles centred at `x, y`, `z` metres up, its long side along `a` — the small boards' roofs and the big boards alike */
  drawFace(ctx: CanvasRenderingContext2D, cam: Camera, x: number, y: number, z: number, a: number, w: number, h: number, ad: FaceAd, lit: boolean): void {
    const [cx, cy] = cam.toScreen(x, y, z), [ux, uy] = cam.toScreen(x + Math.cos(a), y + Math.sin(a), z), [vx, vy] = cam.toScreen(x - Math.sin(a), y + Math.cos(a), z);
    const m: [number, number, number, number, number, number] = [ux - cx, uy - cy, vx - cx, vy - cy, cx, cy];
    if (h * Math.hypot(m[2], m[3]) < 4) { // (far out: the ad's colour is the sign)
      ctx.save(); ctx.transform(...m); ctx.fillStyle = ad.color; ctx.fillRect(-w / 2, -h / 2, w, h); ctx.restore(); return;
    }
    const face = this.face(ad, w / h);
    ctx.save(); ctx.transform(...m); const sm = ctx.imageSmoothingEnabled; ctx.imageSmoothingEnabled = true;
    ctx.drawImage(face as CanvasImageSource, -w / 2, -h / 2, w, h);
    ctx.imageSmoothingEnabled = sm; ctx.restore();
    if (lit) {
      const ex = (Math.abs(m[0]) * w + Math.abs(m[2]) * h) / 2, ey = (Math.abs(m[1]) * w + Math.abs(m[3]) * h) / 2;
      this.lit.push({ m, w, h, face, z, box: [cx - ex, cy - ey, cx + ex, cy + ey] });
    }
  }

  /** a big board: its posts (or its frame on a roof), the face's edge and frame, the face */
  draw(ctx: CanvasRenderingContext2D, cam: Camera, b: PlacedBoard, spec: BoardSpec | undefined, ad: FaceAd | undefined, lit: boolean): void {
    const s = cam.scale, top = b.base + b.lift, post = spec?.post ?? '#5a5f66', frame = spec?.frame ?? '#23272d';
    const ux = Math.cos(b.a), uy = Math.sin(b.a), vx = -uy, vy = ux;
    ctx.save(); ctx.lineCap = 'round';
    if (!b.roof) {
      // two (or three) steel posts, a brace between them under the face, and the catwalk's rail along the front
      ctx.strokeStyle = post; ctx.lineWidth = Math.max(1.5, 0.34 * s);
      const ps = boardPosts(b);
      for (const [px, py] of ps) { const [gx, gy] = cam.toScreen(px, py, b.base), [tx, ty] = cam.toScreen(px, py, top - 0.3); ctx.beginPath(); ctx.moveTo(gx, gy); ctx.lineTo(tx, ty); ctx.stroke(); }
      if (s >= 6) { ctx.lineWidth = Math.max(1, 0.12 * s); ctx.beginPath(); for (let i = 1; i < ps.length; i++) { const [ax, ay] = cam.toScreen(ps[i - 1][0], ps[i - 1][1], top * 0.55), [bx, by] = cam.toScreen(ps[i][0], ps[i][1], top * 0.55); ctx.moveTo(ax, ay); ctx.lineTo(bx, by); } ctx.stroke(); }
    } else {
      // a frame on the roof: four legs a little in from the face's corners
      ctx.strokeStyle = post; ctx.lineWidth = Math.max(1, 0.2 * s); ctx.beginPath();
      for (const [su, sv] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) { const px = b.x + ux * su * (b.w / 2 - 0.8) + vx * sv * (b.d / 2 - 0.5), py = b.y + uy * su * (b.w / 2 - 0.8) + vy * sv * (b.d / 2 - 0.5), [gx, gy] = cam.toScreen(px, py, b.base), [tx, ty] = cam.toScreen(px, py, top - 0.3); ctx.moveTo(gx, gy); ctx.lineTo(tx, ty); }
      ctx.stroke();
    }
    // the face's thickness (its underside, seen past the edge) and its frame, then the face
    const quad = (z: number, grow: number) => { ctx.beginPath(); boardCorners({ ...b, w: b.w + 2 * grow, d: b.d + 2 * grow }).forEach(([px, py], i) => { const [sx, sy] = cam.toScreen(px, py, z); if (i) ctx.lineTo(sx, sy); else ctx.moveTo(sx, sy); }); ctx.closePath(); };
    ctx.fillStyle = shade(frame, 0.8); quad(top - 0.6, 0.1); ctx.fill();
    ctx.fillStyle = frame; quad(top, 0.14); ctx.fill();
    ctx.restore();
    if (ad) this.drawFace(ctx, cam, b.x, b.y, top, b.a, b.w - 0.1, b.d - 0.1, ad, lit);
  }

  /** the board's shadow on the ground (a roof board's falls on its roof: left out) */
  shadow(ctx: CanvasRenderingContext2D, cam: Camera, b: PlacedBoard, sun: number[]): void {
    if (b.roof) return;
    const top = b.base + b.lift, ox = sun[0] * top, oy = sun[1] * top;
    ctx.beginPath(); boardCorners(b).forEach(([x, y], i) => { const [sx, sy] = cam.toScreen(x + ox, y + oy); if (i) ctx.lineTo(sx, sy); else ctx.moveTo(sx, sy); }); ctx.closePath(); ctx.fill();
  }

  /** at night, after the darkness: the lit faces get their light back (a floodlit board reads across the dark town) */
  night(ctx: CanvasRenderingContext2D, n: number): void {
    if (!this.lit.length) return;
    ctx.save(); ctx.globalAlpha = Math.min(1, LIT * n);
    for (const l of this.lit) { ctx.save(); ctx.transform(...l.m); ctx.imageSmoothingEnabled = true; ctx.drawImage(l.face as CanvasImageSource, -l.w / 2, -l.h / 2, l.w, l.h); ctx.restore(); }
    ctx.restore();
  }
}

/** fit `text` in `maxW` at up to `px`, never under `min` (returns the size, or 0 when it does not fit even then) */
function fit(ctx: CanvasRenderingContext2D, text: string, px: number, min: number, maxW: number, font: (f: number) => string): number {
  for (let f = px; f >= min; f *= 0.92) { ctx.font = font(f); if (ctx.measureText(text).width <= maxW) return f; }
  return 0;
}
/** the text in two lines at the size it fits (split at the space nearest the middle) */
function twoLines(ctx: CanvasRenderingContext2D, text: string, px: number, min: number, maxW: number, font: (f: number) => string): { f: number; lines: string[] } | undefined {
  const words = text.split(' '); if (words.length < 2) return undefined;
  let best = 1, bd = Infinity; for (let i = 1; i < words.length; i++) { const d = Math.abs(words.slice(0, i).join(' ').length - words.slice(i).join(' ').length); if (d < bd) { bd = d; best = i; } }
  const lines = [words.slice(0, best).join(' '), words.slice(best).join(' ')];
  for (let f = px; f >= min; f *= 0.92) { ctx.font = font(f); if (lines.every((l) => ctx.measureText(l).width <= maxW)) return { f, lines }; }
  return undefined;
}

/** paint a face: the picture (with the logo and name on a panel at its left), or the logo, name and line on the ad's colours */
export function paintFace(ctx: CanvasRenderingContext2D, W: number, H: number, ad: FaceAd, pic?: CanvasImageSource & { width: number; height: number }): void {
  const bold = (f: number) => `bold ${f}px system-ui, sans-serif`, ital = (f: number) => `italic ${f}px system-ui, sans-serif`;
  ctx.textBaseline = 'middle'; ctx.textAlign = 'left';
  if (pic) {
    const k = Math.max(W / pic.width, H / pic.height), pw = pic.width * k, ph = pic.height * k;
    ctx.drawImage(pic, (W - pw) / 2, (H - ph) / 2, pw, ph);
  }
  if (pic && !ad.plain) {
    const g = ctx.createLinearGradient(0, 0, W * 0.5, 0); g.addColorStop(0, `${ad.color}ee`); g.addColorStop(0.62, `${ad.color}aa`); g.addColorStop(1, `${ad.color}00`);
    ctx.fillStyle = g; ctx.fillRect(0, 0, W * 0.5, H);
    const L = H * 0.46; if (ad.logo) drawSprite(new CanvasPainter(ctx), ad.logo, H * 0.1, H * 0.1, L / Math.max(ad.logo.size[0], ad.logo.size[1]));
    ctx.fillStyle = '#ffffff'; ctx.shadowColor = 'rgba(0,0,0,0.6)'; ctx.shadowBlur = H * 0.04;
    const f = fit(ctx, ad.name, H * 0.2, H * 0.08, W * 0.36, bold); if (f) ctx.fillText(ad.name, H * 0.1, H * 0.76);
    ctx.shadowBlur = 0;
  } else if (!pic) {
    const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, shade(ad.color, 1.15)); g.addColorStop(1, shade(ad.color, 0.85));
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = ad.color2; ctx.fillRect(0, H * 0.93, W, H * 0.07);
    let x0 = H * 0.16;
    if (ad.logo) { const L = H * 0.7; drawSprite(new CanvasPainter(ctx), ad.logo, H * 0.13, (H - L) / 2 - H * 0.02, L / Math.max(ad.logo.size[0], ad.logo.size[1])); x0 = H * 0.13 + L + H * 0.14; }
    const tw = W - x0 - H * 0.1;
    ctx.fillStyle = ad.color2;
    const nf = fit(ctx, ad.name, H * 0.36, H * 0.12, tw, bold);
    if (nf) ctx.fillText(ad.name, x0, ad.line ? H * 0.36 : H * 0.47);
    else { const two = twoLines(ctx, ad.name, H * 0.26, H * 0.1, tw, bold); if (two) { ctx.font = bold(two.f); ctx.fillText(two.lines[0], x0, H * 0.24); ctx.fillText(two.lines[1], x0, H * 0.24 + two.f * 1.05); } }
    if (ad.line) {
      ctx.globalAlpha = 0.92;
      const lf = fit(ctx, ad.line, H * 0.17, H * 0.1, tw, ital);
      if (lf) ctx.fillText(ad.line, x0, H * 0.7);
      else { const two = twoLines(ctx, ad.line, H * 0.14, H * 0.08, tw, ital); if (two) { ctx.font = ital(two.f); ctx.fillText(two.lines[0], x0, H * 0.64); ctx.fillText(two.lines[1], x0, H * 0.64 + two.f * 1.1); } }
      ctx.globalAlpha = 1;
    }
  }
  if (ad.link) { ctx.font = `bold ${Math.round(H * 0.1)}px system-ui, sans-serif`; ctx.textAlign = 'right'; ctx.fillStyle = pic ? '#ffffff' : ad.color2; if (pic) { ctx.shadowColor = 'rgba(0,0,0,0.8)'; ctx.shadowBlur = H * 0.03; } ctx.fillText(ad.link, W - H * 0.08, H * 0.86); ctx.shadowBlur = 0; ctx.textAlign = 'left'; }
}
